import React, { useState } from "react";

import './PasswordInput.css';

// Characters for suggested passwords: no look-alikes (0/O, 1/l/I) and no quotes, spaces or backslashes
const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGITS = '23456789';
const SYMBOLS = '-_!.#*+=?';

function randomIndex( max ){
	const buf = new Uint32Array( 1 );
	// rejection sampling keeps every character equally likely
	const limit = Math.floor( 0x100000000 / max ) * max;
	do{
		window.crypto.getRandomValues( buf );
	} while( buf[ 0 ] >= limit );
	return buf[ 0 ] % max;
}

// A random password with at least one lower case letter, upper case letter, digit and symbol
export function generatePassword( length = 16 ){
	const all = LOWER + UPPER + DIGITS + SYMBOLS;
	const chars = [ LOWER, UPPER, DIGITS, SYMBOLS ].map( set => set[ randomIndex( set.length ) ] );
	while( chars.length < length )
		chars.push( all[ randomIndex( all.length ) ] );
	for( let i = chars.length - 1; i > 0; i-- ){
		const j = randomIndex( i + 1 );
		[ chars[ i ], chars[ j ] ] = [ chars[ j ], chars[ i ] ];
	}
	return chars.join( '' );
}

// Same rules as the backend
export const passwordRules = [
	{ label: 'At least 6 characters', test: p => p.length >= 6 },
	{ label: 'Upper and lower case letters', test: p => /[a-z]/.test( p ) && /[A-Z]/.test( p ) },
	{ label: 'At least one number', test: p => /\d/.test( p ) },
];

const EyeIcon = ({ open }) => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
		<circle cx="12" cy="12" r="3" />
		{ !open && <path d="M4 4l16 16" /> }
	</svg>
);

/*
	Password field with a show/hide button.
	- suggest: adds "Suggest a strong password", which fills the field (and calls onSuggest)
	- showRules: lists the password rules with a tick for each one met
*/
const PasswordInput = ({
	id,
	value,
	onChange,
	placeholder = 'Password',
	autoComplete = 'current-password',
	className = '',
	suggest = false,
	onSuggest,
	showRules = false,
	invalid = false,
	describedBy,
}) => {
	const [ visible, setVisible ] = useState( false );
	const [ suggested, setSuggested ] = useState( '' );
	const [ copied, setCopied ] = useState( false );

	const handleSuggest = () => {
		const password = generatePassword();
		setSuggested( password );
		setVisible( true );
		setCopied( false );
		onChange( password );
		if( onSuggest )
			onSuggest( password );
	};

	const handleCopy = async () => {
		try{
			await navigator.clipboard.writeText( suggested );
			setCopied( true );
		}
		catch( e ){
			setCopied( false );
		}
	};

	const rulesId = id ? id + '-rules' : undefined;
	const describedByIds = [ describedBy, showRules ? rulesId : null ].filter( Boolean ).join( ' ' ) || undefined;

	return (
		<div className="dm-pw">
			<div className="dm-pw-field">
				<input
					id				= { id }
					type			= { visible ? 'text' : 'password' }
					className		= { 'dm-pw-input ' + className }
					placeholder		= { placeholder }
					value			= { value }
					onChange		= { e => { onChange( e.target.value ); if( e.target.value !== suggested ) setSuggested( '' ); } }
					autoComplete	= { autoComplete }
					autoCapitalize	= "off"
					autoCorrect		= "off"
					spellCheck		= { false }
					aria-invalid	= { invalid || undefined }
					aria-describedby = { describedByIds }
				/>
				<button
					type			= "button"
					className		= "dm-pw-eye"
					onClick			= { () => setVisible( !visible ) }
					aria-label		= { visible ? 'Hide password' : 'Show password' }
					aria-pressed	= { visible }
					title			= { visible ? 'Hide password' : 'Show password' }
				>
					<EyeIcon open={ !visible } />
				</button>
			</div>

			{ suggest &&
				<div className="dm-pw-tools">
					<button type="button" className="dm-pw-suggest" onClick={ handleSuggest }>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="8" cy="15" r="4" /><path d="M10.8 12.2L20 3M17 6l3 3M14 9l2 2" /></svg>
						{ suggested ? 'Suggest another' : 'Suggest a strong password' }
					</button>
					{ suggested && value === suggested &&
						<button type="button" className="dm-pw-copy" onClick={ handleCopy }>
							{ copied ? 'Copied' : 'Copy' }
						</button>
					}
				</div>
			}
			{ suggest && suggested && value === suggested &&
				<p className="dm-pw-note" role="status">Save this password somewhere safe, such as a password manager. You will need it to set up email apps.</p>
			}

			{ showRules &&
				<ul className="dm-pw-rules" id={ rulesId }>
					{ passwordRules.map( rule => {
						const ok = rule.test( value || '' );
						return (
							<li key={ rule.label } className={ ok ? 'is-ok' : '' }>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									{ ok ? <path d="M5 12l5 5 9-10" /> : <circle cx="12" cy="12" r="3" /> }
								</svg>
								<span>{ rule.label }<span className="dm-visually-hidden">{ ok ? ' (done)' : ' (missing)' }</span></span>
							</li>
						);
					} ) }
				</ul>
			}
		</div>
	);
};

export default PasswordInput;
